import React, { useRef, useState } from 'react';
import { Card, Text, Box, Heading } from '@radix-ui/themes';
import { IconSvg } from './IconSvg';
import './KanaCard.css';

interface KanaInfo {
    kana: string;
    romaji: string;
    type: 'seion' | 'dakuon' | 'handakuon' | 'yoon';
    position: {
        group: number;
        period: number;
    };
    atomic: number;
    mass?: number;
}

interface KanaCardProps {
    kanaInfo: KanaInfo;
    onClose?: () => void;
    displayMode: 'both' | 'hiragana' | 'katakana';
    correspondingKana?: string; // 对应的平假名或片假名
}

export const KanaCard: React.FC<KanaCardProps> = ({
    kanaInfo,
    onClose,
    displayMode,
    correspondingKana
}) => {
    const [isGifLoading, setIsGifLoading] = useState(true);
    const [gifError, setGifError] = useState(false);
    const [audioError, setAudioError] = useState(false);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const getTypeLabel = (type: KanaInfo['type']) => {
        switch (type) {
            case 'seion': return '清音';
            case 'dakuon': return '浊音';
            case 'handakuon': return '半浊音';
            case 'yoon': return '拗音';
            default: return type;
        }
    };

    const playKanaAudio = () => {
        if (audioRef.current) {
            setAudioError(false);
            audioRef.current.pause();
            audioRef.current.currentTime = 0;

            const audioPath = `/mp3/${kanaInfo.romaji}.mp3`;
            audioRef.current.src = audioPath;
            audioRef.current.play()
                .then(() => {
                    setIsPlayingAudio(true);
                })
                .catch(error => {
                    console.error('播放音频失败:', error);
                    setIsPlayingAudio(false);
                    setAudioError(true);
                });
        }
    };

    // 音频播放结束处理
    React.useEffect(() => {
        const handleAudioEnd = () => {
            setIsPlayingAudio(false);
        };

        const handleAudioError = () => {
            setIsPlayingAudio(false);
            setAudioError(true);
        };

        if (audioRef.current) {
            audioRef.current.addEventListener('ended', handleAudioEnd);
            audioRef.current.addEventListener('error', handleAudioError);
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.removeEventListener('ended', handleAudioEnd);
                audioRef.current.removeEventListener('error', handleAudioError);
            }
        };
    }, []);

    // Reset states when kanaInfo changes
    React.useEffect(() => {
        setIsGifLoading(true);
        setGifError(false);
        setAudioError(false);
        setIsPlayingAudio(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, [kanaInfo]);

    // 判断是平假名还是片假名
    const isKatakana = displayMode === 'katakana' ||
        (displayMode === 'both' && kanaInfo.kana.charCodeAt(0) >= 0x30A0 && kanaInfo.kana.charCodeAt(0) <= 0x30FF);

    const gifPath = isKatakana
        ? `/gif/pian_gif_${kanaInfo.romaji}.gif`
        : `/gif/ping_gif_${kanaInfo.romaji}.gif`;

    return (
        <Card className="kana-card premium-glass">
            {onClose && (
                <button className="close-button" onClick={onClose} aria-label="关闭">
                    <IconSvg name="mdi:close" width={16} height={16} />
                </button>
            )}

            {/* 标题区域 - 大号假名 */}
            <Box className="kana-header">
                <Text className="kana-title">{kanaInfo.kana}</Text>
                <Text className="kana-subtitle">{kanaInfo.romaji}</Text>
            </Box>

            {/* 信息展示 - 居中布局 */}
            <Box className="kana-info-grid">
                <div className="kana-info-item">
                    <Text className="kana-info-value">{kanaInfo.romaji}</Text>
                    <Text className="kana-info-label">罗马音</Text>
                </div>

                {correspondingKana && (
                    <div className="kana-info-item">
                        <Text className="kana-info-value">{correspondingKana}</Text>
                        <Text className="kana-info-label">
                            {isKatakana ? '平假名' : '片假名'}
                        </Text>
                    </div>
                )}

                <div className="kana-info-item">
                    <Text className="kana-info-value">{getTypeLabel(kanaInfo.type)}</Text>
                    <Text className="kana-info-label">类型</Text>
                </div>
            </Box>

            {/* 笔顺GIF */}
            <Box className="kana-stroke-section">
                <Heading as="h3" size="2" className="section-title">笔顺</Heading>
                <div className="stroke-gif-container">
                    {isGifLoading && (
                        <div className="loading-indicator">加载中...</div>
                    )}
                    {gifError && (
                        <div className="error-message">无法加载笔顺</div>
                    )}
                    {!gifError && (
                        <img
                            src={gifPath}
                            alt={`${kanaInfo.kana}的笔顺`}
                            onError={(e) => {
                                const imgElement = e.target as HTMLImageElement;
                                imgElement.src = `/gif/gif_${kanaInfo.romaji}.gif`;

                                if (imgElement.dataset.retried) {
                                    setGifError(true);
                                } else {
                                    imgElement.dataset.retried = 'true';
                                }
                            }}
                            onLoad={() => setIsGifLoading(false)}
                            style={{ display: isGifLoading ? 'none' : 'block' }}
                        />
                    )}
                </div>
            </Box>

            {/* 音频播放 */}
            <Box className="kana-audio-section">
                <button
                    className={`play-audio-button ${isPlayingAudio ? 'playing' : ''} ${audioError ? 'error' : ''}`}
                    onClick={playKanaAudio}
                    disabled={isPlayingAudio || audioError}
                >
                    <IconSvg
                        name={isPlayingAudio ? "ph:speaker-high-fill" : "ph:speaker-high"}
                        width={20}
                        height={20}
                    />
                    <span>{isPlayingAudio ? '播放中...' : audioError ? '读音不可用' : '播放读音'}</span>
                </button>
            </Box>

            <audio ref={audioRef} preload="none" />
        </Card>
    );
};
